import { FunctionComponent, PropsWithChildren, ReactNode, useMemo } from 'react';
import styles from './styles.module.scss';
import { CheckoutState } from '@shared/types';
import Button from '../../Button';
import { BookingCheckoutSummary } from '../CheckoutSummary/BookingCheckoutSummary';
import { OrderCheckoutSummary } from '../CheckoutSummary/OrderCheckoutSummary';
import { RequestCheckoutSummary } from '../CheckoutSummary/RequestCheckoutSummary';
import { BookingRequestCheckoutSummary } from '../CheckoutSummary/BookingRequestCheckoutSummary';
import { ArrowLeftIcon, ArrowRightIcon } from '../../Icons';
import { PoweredBy } from '../../PoweredBy';
import { PublicCourse } from '@cademy-package/sdk-component-types';

export type StepContainerProps = PropsWithChildren<{
    course: PublicCourse;
    optimisticCheckoutState: CheckoutState;
    onContinueClick?: () => unknown;
    continueDisabled?: boolean;
    onBackClick?: () => unknown;
}>;

export const StepContainer: FunctionComponent<StepContainerProps> = ({
    course,
    optimisticCheckoutState,
    onContinueClick,
    continueDisabled,
    onBackClick,
    children,
}) => {
    const summary = useMemo((): ReactNode => {
        switch (optimisticCheckoutState.flow) {
            case 'booking': {
                if (course.type !== 'blended' && course.type !== 'class') {
                    throw new Error(`Invalid booking course type ${course.type}`);
                }
                return (
                    <BookingCheckoutSummary
                        course={course}
                        checkoutState={optimisticCheckoutState}
                    />
                );
            }
            case 'order': {
                if (course.type !== 'on-demand') {
                    throw new Error(`Invalid booking course type ${course.type}`);
                }
                return (
                    <OrderCheckoutSummary course={course} checkoutState={optimisticCheckoutState} />
                );
            }
            case 'request': {
                if (course.type !== 'on-request') {
                    throw new Error(`Invalid booking course type ${course.type}`);
                }
                return (
                    <RequestCheckoutSummary
                        course={course}
                        checkoutState={optimisticCheckoutState}
                    />
                );
            }
            case 'dateBookingRequest':
            case 'groupBookingRequest': {
                if (course.type !== 'blended' && course.type !== 'class') {
                    throw new Error(`Invalid booking course type ${course.type}`);
                }
                return (
                    <BookingRequestCheckoutSummary
                        course={course}
                        checkoutState={optimisticCheckoutState}
                    />
                );
            }
            case 'bookingApplication': {
                if (course.type !== 'blended' && course.type !== 'class') {
                    throw new Error(`Invalid booking course type ${course.type}`);
                }
                return (
                    <BookingCheckoutSummary
                        course={course}
                        checkoutState={optimisticCheckoutState}
                    />
                );
            }
        }
        return null;
    }, [course, optimisticCheckoutState]);
    return (
        <div className={styles.container}>
            <div className={styles.step}>{children}</div>
            <div className={styles.summary}>
                {summary}
                {onContinueClick ? (
                    <Button
                        preset="primary"
                        className={styles.progressButton}
                        disabled={continueDisabled}
                        onClick={onContinueClick}
                    >
                        Continue <ArrowRightIcon />
                    </Button>
                ) : null}
                <div className={styles.poweredByCademyBanner}>
                    <PoweredBy origin={optimisticCheckoutState.origin} />
                </div>
            </div>
            <div className={styles.mobileProgressButtonsSection}>
                <div className={styles.mobileProgressButtons}>
                    {onBackClick ? (
                        <Button preset="default" onClick={onBackClick}>
                            <ArrowLeftIcon /> Back
                        </Button>
                    ) : (
                        <span></span>
                    )}
                    {onContinueClick ? (
                        <Button
                            preset="primary"
                            disabled={continueDisabled}
                            onClick={onContinueClick}
                        >
                            Continue <ArrowRightIcon />
                        </Button>
                    ) : (
                        <span></span>
                    )}
                </div>
                <PoweredBy origin={optimisticCheckoutState.origin} />
            </div>
        </div>
    );
};
